import { useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../api/axios';

function todayIsoDate() {
  const now = new Date();
  const pad = (num) => String(num).padStart(2, '0');
  return [now.getFullYear(), pad(now.getMonth() + 1), pad(now.getDate())].join('-');
}

const TEMPLATE_COLUMNS = [
  { header: 'رقم اللوحة', sample: '4970' },
  { header: 'رمز اللوحة', sample: 'ب ح ا' },
  { header: 'ماركة المركبة', sample: 'لاندروفر' },
  { header: 'طراز المركبة', sample: 'ايفوك' },
  { header: 'الهيكل', sample: 'SALZA2BX6PH207717' },
  { header: 'الموديل', sample: '2023' },
  { header: 'عدد المفاتيح', sample: '2' },
  { header: 'المالك', sample: 'محمد العتيبي' },
  { header: 'ملاحظات', sample: 'خدش في الباب الأيمن' },
];

const INSTRUCTIONS = [
  'الملف يجب أن يكون بصيغة Excel بامتداد xlsx.',
  'الصف الأول عناوين الأعمدة — لا تحذفه ولا تغيّر ترتيب الأعمدة.',
  'صف واحد لكل مركبة، وبلا صفوف فارغة بين الصفوف.',
  'جميع الأعمدة مطلوبة عدا «ملاحظات» — أي صف فيه خانة فارغة يُرفض مع بيان العمود الناقص.',
  'رقم الهيكل هو ما يميّز المركبة، ولا يُقبل مكررًا داخل الملف ولا مسجّلًا مسبقًا في النظام.',
  'رقم اللوحة ورمزها في عمودين منفصلين: الأرقام في «رقم اللوحة» والحروف في «رمز اللوحة».',
  'ماركة المركبة وطراز المركبة يجب أن تُكتبا كما هما مسجّلان في النظام حرفيًا.',
  'الموديل هو سنة الصنع بأربعة أرقام (مثال: 2023)، ويُسجَّل تلقائيًا إن لم يكن موجودًا.',
  'عدد المفاتيح يُكتب كما هو مسجّل في خيارات «عدد المفاتيح» في النظام.',
  'المزاد يُؤخذ تلقائيًا من المزاد المسجَّل دخولك عليه، فلا حاجة لعمود له في الملف.',
];

export default function ImportInspectionsModal({ open, onClose, onImported }) {
  const [downloading, setDownloading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [file, setFile] = useState(null);
  const [inspectionDate, setInspectionDate] = useState(todayIsoDate);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  if (!open) return null;

  const close = () => {
    setFile(null);
    setResult(null);
    setError('');
    onClose();
  };

  const pickFile = (event) => {
    setFile(event.target.files?.[0] || null);
    setResult(null);
    setError('');
  };

  const runImport = async () => {
    if (!file) return;

    setImporting(true);
    setError('');
    setResult(null);

    try {
      const form = new FormData();
      form.append('file', file);
      form.append('inspection_date', inspectionDate);

      const { data } = await api.post('/inspections/import', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });

      setResult(data);
      if (data?.created_count > 0) onImported?.();
    } catch (err) {
      const payload = err?.response?.data;
      setError(payload?.message || err?.userMessage || 'تعذّر استيراد الملف. حاول مرة أخرى.');

      // رفض الملف كاملًا يأتي بقائمة الأسطر المعطوبة، فنعرضها كما نعرض نتيجة النجاح.
      if (Array.isArray(payload?.skipped)) setResult(payload);
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = async () => {
    setDownloading(true);
    setError('');

    try {
      const response = await api.get('/inspections/import-template', {
        responseType: 'blob',
        timeout: 60000,
      });

      const url = window.URL.createObjectURL(new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'inspections-import-template.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();

      // لا نلغي الرابط فورًا حتى لا ينتج ملف تالف في بعض المتصفحات.
      window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch {
      setError('تعذّر تنزيل النموذج. حاول مرة أخرى.');
    } finally {
      setDownloading(false);
    }
  };

  // نرسم النافذة في body مباشرة: الحاوية <main> في AppShell تُنشئ سياق تكديس
  // مستقلًا (isolation: isolate) فيبقى z-50 حبيس داخله ويغطّي الهيدر أعلى النافذة.
  return createPortal(
    <div className="fixed inset-0 z-[100] overflow-y-auto bg-black/40 p-4">
      <div className="mx-auto my-6 w-full max-w-3xl rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-slate-900">استيراد فحوصات</h3>
            <p className="mt-1 text-sm text-slate-500">
              جهّز ملف الإكسل حسب التعليمات التالية قبل رفعه.
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            className="rounded-lg border border-slate-300 px-3 py-1 text-sm text-slate-600 hover:bg-slate-50"
          >
            إغلاق
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <ol className="mb-5 list-decimal space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-4 pr-8 text-sm text-slate-700">
          {INSTRUCTIONS.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ol>

        <div className="mb-2 text-sm font-semibold text-slate-700">تنسيق الملف المطلوب</div>
        <div className="mb-5 overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-right text-xs">
            <thead className="bg-slate-100">
              <tr>
                {TEMPLATE_COLUMNS.map((column) => (
                  <th key={column.header} className="whitespace-nowrap px-3 py-2 font-semibold text-slate-700">
                    {column.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {TEMPLATE_COLUMNS.map((column) => (
                  <td key={column.header} className="whitespace-nowrap px-3 py-2 text-slate-600">
                    {column.sample}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        <button
          type="button"
          onClick={downloadTemplate}
          disabled={downloading}
          className="rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          {downloading ? 'جاري التنزيل...' : '⬇ تنزيل نموذج الإكسل'}
        </button>

        <div className="mt-6 border-t pt-5">
          <div className="mb-2 text-sm font-semibold text-slate-700">رفع الملف بعد تعبئته</div>
          <div className="flex flex-wrap items-end gap-3">
            <label className="min-w-[220px] flex-1">
              <span className="mb-1 block text-xs font-semibold text-slate-600">ملف الإكسل</span>
              <input
                type="file"
                accept=".xlsx"
                onChange={pickFile}
                className="w-full rounded-xl border border-slate-300 p-2 text-sm file:ml-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm file:text-slate-700"
              />
            </label>

            <label>
              <span className="mb-1 block text-xs font-semibold text-slate-600">تاريخ الفحص</span>
              <input
                type="date"
                value={inspectionDate}
                onChange={(event) => setInspectionDate(event.target.value)}
                className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-primary"
              />
            </label>

            <button
              type="button"
              onClick={runImport}
              disabled={!file || importing}
              className="rounded-xl bg-primary px-5 py-2.5 text-sm font-bold text-white hover:opacity-90 disabled:opacity-50"
            >
              {importing ? 'جاري الاستيراد...' : 'استيراد'}
            </button>
          </div>
          <p className="mt-2 text-xs text-slate-500">
            يُطبَّق هذا التاريخ على جميع الفحوصات الموجودة في الملف.
          </p>
        </div>

        {result && (
          <div className="mt-4 space-y-3">
            <div className="flex flex-wrap gap-3 text-sm">
              <span className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 font-semibold text-emerald-800">
                تم إدخال {result.created_count} فحصًا
              </span>
              {result.skipped_count > 0 && (
                <span className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 font-semibold text-amber-800">
                  رُفض {result.skipped_count} صفًا
                </span>
              )}
            </div>

            {result.skipped_count > 0 && (
              <div className="max-h-52 overflow-auto rounded-xl border border-amber-200 bg-amber-50 p-3">
                <ul className="space-y-1.5 text-xs text-amber-900">
                  {result.skipped.map((item) => (
                    <li key={`${item.row}-${item.vin}`}>
                      <span className="font-bold">صف {item.row}:</span> {item.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
